import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { 
    Brain, 
    Plus, 
    Trash2, 
    Settings2, 
    Activity, 
    Database, 
    Save, 
    AlertCircle, 
    CheckCircle2, 
    GripVertical,
    Zap,
    History,
    MessageSquare,
    Globe,
    Lock,
    RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableItem = ({ id, provider, onEdit, onDelete, onToggle }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`
                group bg-zinc-900/40 border border-zinc-800/50 p-4 rounded-3xl flex items-center gap-4 
                hover:border-blue-500/30 transition-all active:scale-[0.98]
                ${!provider.is_active ? 'opacity-60 grayscale' : ''}
            `}
        >
            <button {...attributes} {...listeners} className="p-2 text-zinc-600 hover:text-zinc-400 cursor-grab active:cursor-grabbing">
                <GripVertical size={18} />
            </button>
            
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-white shadow-lg
                ${provider.provider_type === 'openrouter' ? 'bg-indigo-600 shadow-indigo-600/20' : 
                  provider.provider_type === 'google' ? 'bg-emerald-600 shadow-emerald-600/20' :
                  provider.provider_type === 'openai' ? 'bg-zinc-700 shadow-zinc-700/20' : 'bg-blue-600 shadow-blue-600/20'}
            `}>
                {provider.provider_type ? provider.provider_type[0].toUpperCase() : '?'}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <h3 className="font-bold text-white truncate text-base">{provider.name}</h3>
                    {provider.is_active && (
                         <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                    )}
                </div>
                <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 bg-zinc-800/50 px-2 py-0.5 rounded-md">
                        {provider.provider_type}
                    </span>
                    <span className="text-[10px] font-bold text-zinc-600 truncate">
                        {provider.model_name || 'default model'}
                    </span>
                    <span className="text-[10px] font-mono text-zinc-700">
                        {provider.api_key}
                    </span>
                </div>
            </div>

            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                    onClick={() => onToggle(provider)}
                    className={`p-2 rounded-xl transition-colors ${provider.is_active ? 'text-zinc-500 hover:text-zinc-300' : 'text-emerald-500 hover:text-emerald-400'}`}
                >
                    <Zap size={18} />
                </button>
                <button 
                    onClick={() => onEdit(provider)}
                    className="p-2 text-zinc-500 hover:text-blue-400 rounded-xl transition-colors"
                >
                    <Settings2 size={18} />
                </button>
                <button 
                    onClick={() => onDelete(provider.id)}
                    className="p-2 text-zinc-500 hover:text-rose-400 rounded-xl transition-colors"
                >
                    <Trash2 size={18} />
                </button>
            </div>
        </div>
    );
};

const UserBotTab = () => {
    const [phone, setPhone] = useState('');
    const [apiId, setApiId] = useState('');
    const [apiHash, setApiHash] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [phoneCodeHash, setPhoneCodeHash] = useState(null);
    const [channelUsername, setChannelUsername] = useState('');
    const [step, setStep] = useState(1); // 1: API credentials, 2: Code, 3: Password

    const { data: config, isLoading } = useQuery({
        queryKey: ['userbot-config'],
        queryFn: async () => (await client.get('/userbot/config')).data
    });

    const requestCodeMutation = useMutation({
        mutationFn: (data) => client.post('/userbot/request-code', data),
        onSuccess: (res) => {
            if (res.data.status === 'ok') {
                setPhoneCodeHash(res.data.phone_code_hash);
                toast.success('Код подтверждения отправлен в ваш Telegram!');
            } else {
                toast.error('Ошибка Telegram: ' + (res.data.message || 'Неизвестная ошибка'));
            }
        },
        onError: (err) => toast.error('Ошибка сети/API: ' + (err.response?.data?.detail || err.message))
    });

    const verifyCodeMutation = useMutation({
        mutationFn: (data) => {
            // Убеждаемся что поле называется phone_code_hash для бэкенда
            const payload = { 
                ...data, 
                phone_code_hash: phoneCodeHash 
            };
            return client.post('/userbot/verify-code', payload);
        },
        onSuccess: (res) => {
            if (res.data.status === 'ok') {
                setPhoneCodeHash(null);
                setCode('');
                toast.success('UserBot успешно подключен!');
                setTimeout(() => window.location.reload(), 1500);
            } else if (res.data.status === 'password_needed') {
                setStep(3);
                toast.info('Требуется двухфакторная аутентификация (2FA)');
            } else {
                toast.error('Ошибка верификации: ' + (res.data.message || 'Неверный код'));
            }
        },
        onError: (err) => toast.error('Критическая ошибка: ' + (err.response?.data?.detail || err.message))
    });

    const verifyPasswordMutation = useMutation({
        mutationFn: (data) => client.post('/userbot/verify-password', data),
        onSuccess: (res) => {
            if (res.data.status === 'ok') {
                toast.success('UserBot успешно подключен (2FA ок)!');
                setTimeout(() => window.location.reload(), 1500);
            } else {
                toast.error('Ошибка пароля: ' + (res.data.message || 'Неверный пароль'));
            }
        },
        onError: (err) => toast.error('Ошибка 2FA: ' + (err.response?.data?.detail || err.message))
    });

    const syncMutation = useMutation({
        mutationFn: (data) => client.post('/userbot/sync', data),
        onSuccess: (res) => {
            toast.success(`Успешно! Синхронизировано: ${res.data.total_synced}. Новых: ${res.data.new_members}. Ушло: ${res.data.left_members}`);
        },
        onError: (err) => toast.error('Ошибка синхронизации: ' + (err.response?.data?.detail || err.message))
    });

    if (isLoading) return <div className="h-64 flex items-center justify-center"><RefreshCw className="animate-spin text-blue-500" /></div>;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-12 space-y-6">
                <div className="bg-zinc-900/50 border border-zinc-800/50 p-8 rounded-[2.5rem] shadow-2xl">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold shadow-lg ${config?.is_active ? 'bg-emerald-600 shadow-emerald-500/20' : 'bg-zinc-800 shadow-zinc-900/50'}`}>
                                <Zap className="text-white w-6 h-6" />
                            </div>
                            <div>
                                <h2 className="text-lg font-black text-white uppercase italic">Telegram UserBot</h2>
                                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mt-1">
                                    {config?.is_active ? `Активен (Сессия: ${config.session_status})` : 'Бот не подключен'}
                                </p>
                            </div>
                        </div>

                        {config?.is_active && (
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Last Sync:</span>
                                <span className="text-[10px] font-bold text-white px-3 py-1 bg-zinc-800 rounded-full">
                                    {config.last_sync ? new Date(config.last_sync).toLocaleString() : 'Never'}
                                </span>
                            </div>
                        )}
                    </div>

                    {!config?.is_active && step === 1 ? (
                        <div className="space-y-6 max-w-2xl mx-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">API ID</label>
                                    <input 
                                        value={apiId}
                                        onChange={(e) => setApiId(e.target.value)}
                                        className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold placeholder:text-zinc-700 font-mono" 
                                        placeholder="1234567" 
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">API HASH</label>
                                    <input 
                                        value={apiHash}
                                        onChange={(e) => setApiHash(e.target.value)}
                                        className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold placeholder:text-zinc-700 font-mono" 
                                        placeholder="abcdef123..." 
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Номер телефона</label>
                                <input 
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold placeholder:text-zinc-700" 
                                    placeholder="+79001234567" 
                                />
                            </div>
                            <button 
                                onClick={() => requestCodeMutation.mutate({ api_id: apiId, api_hash: apiHash, phone })}
                                disabled={requestCodeMutation.isLoading}
                                className="w-full bg-blue-600 hover:bg-blue-500 text-white p-5 rounded-2xl font-black uppercase tracking-widest text-sm transition-all shadow-xl shadow-blue-600/20 active:scale-95 disabled:opacity-50"
                            >
                                {requestCodeMutation.isLoading ? 'Отправка...' : 'Отправить код'}
                            </button>
                        </div>
                    ) : !config?.is_active && step === 3 ? (
                        <div className="space-y-6 max-w-md mx-auto text-center">
                            <div className="space-y-1.5 text-center">
                                <div className="p-4 bg-amber-500/10 rounded-2xl border border-amber-500/20 mb-4">
                                     <Lock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                                     <h3 className="text-sm font-black uppercase text-amber-500">Двухэтапная аутентификация</h3>
                                     <p className="text-[10px] text-zinc-500 mt-1">Введите ваш облачный пароль Telegram</p>
                                </div>
                                <input 
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-zinc-950 border border-zinc-800 p-6 rounded-3xl text-center text-xl font-bold text-white outline-none focus:border-blue-500 transition-all" 
                                    placeholder="Ваш пароль..." 
                                />
                            </div>
                            <button 
                                onClick={() => verifyPasswordMutation.mutate({ 
                                    password, 
                                    api_id: apiId, 
                                    api_hash: apiHash, 
                                    phone 
                                })}
                                disabled={verifyPasswordMutation.isLoading}
                                className="w-full bg-blue-600 hover:bg-blue-500 text-white p-5 rounded-2xl font-black uppercase tracking-widest text-sm transition-all shadow-xl shadow-blue-600/20 active:scale-95 disabled:opacity-50"
                            >
                                {verifyPasswordMutation.isLoading ? 'Проверка...' : 'Войти'}
                            </button>
                            <button onClick={() => setStep(2)} className="text-zinc-500 text-xs font-bold uppercase hover:text-zinc-300">Назад к коду</button>
                        </div>
                    ) : !config?.is_active && phoneCodeHash ? (
                        <div className="space-y-6 max-w-md mx-auto text-center">
                            <div className="space-y-1.5">
                                <label className="text-sm font-black uppercase tracking-widest text-zinc-500">Код подтверждения</label>
                                <input 
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    className="w-full bg-zinc-950 border border-zinc-800 p-6 rounded-3xl text-center text-3xl font-black text-white tracking-[0.5em] outline-none focus:border-blue-500 transition-all shadow-inner" 
                                    placeholder="00000" 
                                />
                            </div>
                            <button 
                                onClick={() => verifyCodeMutation.mutate({ code, phone_code_hash: phoneCodeHash, phone, api_id: apiId, api_hash: apiHash })}
                                disabled={verifyCodeMutation.isLoading}
                                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white p-5 rounded-2xl font-black uppercase tracking-widest text-sm transition-all shadow-xl shadow-emerald-600/20 active:scale-95 disabled:opacity-50"
                            >
                                {verifyCodeMutation.isLoading ? 'Проверка...' : 'Подтвердить вход'}
                            </button>
                            <button onClick={() => setPhoneCodeHash(null)} className="text-zinc-500 text-xs font-bold uppercase hover:text-zinc-300">Изменить номер</button>
                        </div>
                    ) : (
                        <div className="space-y-8 animate-in pulse duration-[2s] repeat-infinite">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-zinc-950/50 border border-zinc-800/50 p-6 rounded-3xl">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
                                        <History size={12} className="text-blue-500" /> Ручная синхронизация
                                    </h4>
                                    <div className="space-y-4">
                                        <div className="relative">
                                            <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" size={16} />
                                            <input 
                                                value={channelUsername}
                                                onChange={(e) => setChannelUsername(e.target.value)}
                                                className="w-full bg-zinc-900 border border-zinc-800 py-4 pl-12 pr-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold placeholder:font-medium italic" 
                                                placeholder="username нашего канала" 
                                            />
                                        </div>
                                        <button 
                                            onClick={() => syncMutation.mutate({ channel_username: channelUsername })}
                                            disabled={syncMutation.isLoading || !channelUsername}
                                            className="w-full bg-gradient-to-r from-blue-700 to-indigo-700 hover:brightness-110 text-white p-4 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all shadow-xl shadow-indigo-600/20 flex items-center justify-center gap-3 active:scale-[0.98] disabled:opacity-30"
                                        >
                                            {syncMutation.isLoading ? <RefreshCw className="animate-spin" size={16} /> : <Activity size={16} />}
                                            Запустить сканирование участников
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-zinc-950/50 border border-zinc-800/50 p-6 rounded-3xl flex flex-col justify-center">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                                        <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Статус сессии: В сети</span>
                                    </div>
                                    <p className="text-xs text-zinc-500 italic leading-relaxed">
                                        Сессия UserBot активна. Теперь вы можете синхронизировать список участников канала, нажав кнопку слева. Система автоматически определит новых и ушедших.
                                    </p>
                                    <button 
                                        onClick={async () => {
                                            if (window.confirm("Отключить сессию?")) {
                                                await client.post('/userbot/logout');
                                                window.location.reload();
                                            }
                                        }}
                                        className="mt-6 text-[10px] font-black uppercase tracking-widest text-rose-500/50 hover:text-rose-500 text-left"
                                    >
                                        [ Отключить UserBot ]
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Warning Card */}
                <div className="bg-amber-600/5 border border-amber-600/20 p-6 rounded-[2rem] flex items-start gap-4">
                    <div className="p-3 bg-amber-600/10 rounded-2xl text-amber-500">
                        <AlertCircle size={24} />
                    </div>
                    <div>
                        <h4 className="font-black text-white uppercase italic text-sm mb-1 tracking-tight">Важное предупреждение</h4>
                        <p className="text-xs text-zinc-500 font-medium leading-relaxed">
                            UserBot имитирует действия человека. Не запускайте синхронизацию слишком часто (рекомендуется 1-2 раза в день), чтобы Telegram не ограничил вашу учетную запись. 
                            Используйте отдельный аккаунт, если боитесь за основной профиль.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AISettings = () => {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('providers');
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [editingPrompt, setEditingPrompt] = useState(null);

    const { data: providers, isLoading: providersLoading } = useQuery({
        queryKey: ['ai-providers'],
        queryFn: async () => (await client.get('/ai/providers')).data,
        onError: (err) => {
            console.error('Providers fetch error', err);
            toast.error('Ошибка загрузки AI провайдеров: ' + (err.response?.data?.detail || err.message));
        }
    });

    const { data: logs } = useQuery({
        queryKey: ['ai-logs'],
        queryFn: async () => (await client.get('/ai/logs')).data,
        onError: (err) => {
            console.error('Logs fetch error', err);
            toast.error('Ошибка загрузки логов AI: ' + (err.response?.data?.detail || err.message));
        }
    });

    const { data: prompts } = useQuery({
        queryKey: ['ai-prompts'],
        queryFn: async () => (await client.get('/ai/prompts')).data
    });

    const configMutation = useMutation({
        mutationFn: (data) => client.post('/ai/providers', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['ai-providers']);
            setIsConfigModalOpen(false);
            setEditingConfig(null);
            toast.success('Настройки сохранены');
        }
    });

    const reorderMutation = useMutation({
        mutationFn: (ids) => client.post('/ai/providers/reorder', ids),
        onSuccess: () => queryClient.invalidateQueries(['ai-providers'])
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => client.delete(`/ai/providers/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries(['ai-providers']);
            toast.success('Провайдер удален');
        }
    });

    const promptMutation = useMutation({
        mutationFn: (data) => client.post('/ai/prompts', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['ai-prompts']);
            setIsPromptModalOpen(false);
            setEditingPrompt(null);
            toast.success('Шаблон сохранен');
        }
    });

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = providers.findIndex(p => p.id === active.id);
            const newIndex = providers.findIndex(p => p.id === over.id);
            const newProviders = arrayMove(providers, oldIndex, newIndex);
            reorderMutation.mutate(newProviders.map(p => p.id));
        }
    };

    const handleOpenEdit = (p) => {
        setEditingConfig(p);
        setIsConfigModalOpen(true);
    };

    const handleToggle = (p) => {
        configMutation.mutate({ ...p, is_active: !p.is_active });
    };

    return (
        <div className="p-1 md:p-6 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-zinc-900/50 border border-zinc-800/50 p-6 rounded-[2.5rem] shadow-2xl backdrop-blur-md">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl flex items-center justify-center shadow-2xl shadow-blue-500/20 rotate-3">
                        <Brain className="text-white w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-white tracking-tight italic uppercase">ИИ Настройки</h1>
                        <p className="text-zinc-500 font-bold text-xs uppercase tracking-widest mt-1">Multi-Channel Intelligence Hub</p>
                    </div>
                </div>
                <div className="flex bg-zinc-950/50 p-1.5 rounded-2xl border border-zinc-800/50">
                    <button 
                        onClick={() => setActiveTab('providers')}
                        className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'providers' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-zinc-500 hover:text-white'}`}
                    >
                        Провайдеры
                    </button>
                    <button 
                        onClick={() => setActiveTab('prompts')}
                        className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'prompts' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-zinc-500 hover:text-white'}`}
                    >
                        Шаблоны
                    </button>
                    <button 
                        onClick={() => setActiveTab('logs')}
                        className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'logs' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-zinc-500 hover:text-white'}`}
                    >
                        Логи
                    </button>
                    <button 
                        onClick={() => setActiveTab('userbot')}
                        className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'userbot' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-zinc-500 hover:text-white'}`}
                    >
                        UserBot
                    </button>
                </div>
            </header>

            {activeTab === 'providers' && (
                <main className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Providers List */}
                    <div className="lg:col-span-12 space-y-6">
                        <div className="flex items-center justify-between px-2">
                            <div className="flex items-center gap-2">
                                <Zap className="text-blue-500 w-5 h-5" />
                                <h2 className="text-lg font-black text-white uppercase italic">Активные модели</h2>
                            </div>
                            <button 
                                onClick={() => { setEditingConfig(null); setIsConfigModalOpen(true); }}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20 active:scale-95"
                            >
                                <Plus size={14} strokeWidth={3} />
                                Добавить
                            </button>
                        </div>

                        <div className="space-y-3">
                            {providersLoading ? (
                                [1,2,3].map(i => <div key={i} className="h-20 bg-zinc-900 animate-pulse rounded-3xl border border-zinc-800" />)
                            ) : (
                                <DndContext 
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext 
                                        items={providers?.map(p => p.id) || []}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {(providers || []).map((provider) => (
                                            <SortableItem 
                                                key={provider.id} 
                                                id={provider.id} 
                                                provider={provider} 
                                                onEdit={handleOpenEdit}
                                                onDelete={(id) => window.confirm('Удалить?') && deleteMutation.mutate(id)}
                                                onToggle={handleToggle}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            )}
                            
                            {!providersLoading && (!providers || providers.length === 0) && (
                                <div className="p-12 bg-zinc-900/30 border border-zinc-800/50 border-dashed rounded-[3rem] text-center">
                                    <div className="w-16 h-16 bg-zinc-800/50 rounded-3xl flex items-center justify-center mx-auto mb-4">
                                        <Database className="text-zinc-600 w-8 h-8" />
                                    </div>
                                    <p className="text-zinc-500 font-bold text-sm uppercase tracking-widest">Провайдеры не настроены</p>
                                </div>
                            )}
                        </div>

                        {/* Failover Info */}
                        <div className="bg-blue-600/5 border border-blue-600/20 p-6 rounded-[2rem] flex items-start gap-4">
                            <div className="p-3 bg-blue-600/10 rounded-2xl text-blue-500">
                                <AlertCircle size={24} />
                            </div>
                            <div>
                                <h4 className="font-black text-white uppercase italic text-sm mb-1 tracking-tight">Интеллектуальный Failover</h4>
                                <p className="text-xs text-zinc-500 font-medium leading-relaxed">
                                    Система автоматически опрашивает провайдеров сверху вниз. Если основной провайдер (OpenRouter) вернет ошибку или таймаут, 
                                    запрос будет автоматически перенаправлен на следующего доступного провайдера (Google Gemini или OpenAI).
                                </p>
                            </div>
                        </div>
                    </div>
                </main>
            )}

            {activeTab === 'prompts' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <button 
                        onClick={() => { setEditingPrompt(null); setIsPromptModalOpen(true); }}
                        className="group h-[200px] border-2 border-dashed border-zinc-800 hover:border-blue-500/50 rounded-[2.5rem] flex flex-col items-center justify-center gap-3 transition-all hover:bg-blue-600/5"
                    >
                        <div className="w-12 h-12 rounded-2xl bg-zinc-900 group-hover:bg-blue-600 transition-colors flex items-center justify-center">
                            <Plus className="text-zinc-500 group-hover:text-white" size={24} />
                        </div>
                        <span className="font-black uppercase tracking-widest text-[10px] text-zinc-500 group-hover:text-blue-500 transition-colors">Новый шаблон</span>
                    </button>
                    
                    {prompts?.map(prompt => (
                        <div key={prompt.slug} className="group bg-zinc-900/50 border border-zinc-800/50 p-6 rounded-[2.5rem] hover:border-blue-500/30 transition-all flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 bg-blue-600/10 rounded-2xl text-blue-500">
                                    <MessageSquare size={20} />
                                </div>
                                <button 
                                    onClick={() => { setEditingPrompt(prompt); setIsPromptModalOpen(true); }}
                                    className="p-2 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-white transition-all"
                                >
                                    <Settings2 size={18} />
                                </button>
                            </div>
                            <h3 className="font-black text-white italic text-lg mb-1">{prompt.name}</h3>
                            <p className="text-xs text-zinc-500 font-bold uppercase tracking-widest mb-3">{prompt.slug}</p>
                            <p className="text-xs text-zinc-400 line-clamp-3 mb-6 flex-1 font-medium leading-relaxed">{prompt.template}</p>
                            <div className="flex items-center gap-2 mt-auto">
                                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${prompt.is_system ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500'}`}>
                                    {prompt.is_system ? 'System' : 'User'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {activeTab === 'logs' && (
                <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-[2.5rem] overflow-hidden">
                    <div className="p-6 border-b border-zinc-800/50 flex justify-between items-center bg-zinc-950/20">
                        <div className="flex items-center gap-2">
                            <History className="text-zinc-500 w-5 h-5" />
                            <h2 className="text-sm font-black text-white uppercase italic">История запросов</h2>
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">Последние 50</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-zinc-950/30">
                                <tr>
                                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-500">Дата</th>
                                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-500">Провайдер</th>
                                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-500">Промпт</th>
                                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-500">Статус</th>
                                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-zinc-500">Длительность</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-800/30">
                                {logs?.map((log, i) => (
                                    <tr key={i} className="hover:bg-zinc-800/20 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="text-xs text-zinc-400 font-bold whitespace-nowrap">
                                                {new Date(log.timestamp).toLocaleString()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-xs text-white font-black italic">{log.provider_name || 'N/A'}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-xs text-zinc-500 font-medium truncate max-w-[200px] block">
                                                {log.prompt_slug || 'manual_request'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {log.status === 'success' ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-500 rounded-full text-[10px] font-black uppercase tracking-tight">
                                                    <CheckCircle2 size={10} /> OK
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-500/10 text-rose-500 rounded-full text-[10px] font-black uppercase tracking-tight">
                                                    <AlertCircle size={10} /> Error
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-[10px] font-mono text-zinc-500">{log.duration_ms}ms</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'userbot' && <UserBotTab />}

            {/* Config Modal */}
            {isConfigModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-zinc-900 border border-zinc-800 rounded-[3rem] w-full max-w-lg shadow-2xl p-8 relative">
                        <h2 className="text-2xl font-black text-white uppercase italic italic mb-6">
                            {editingConfig ? 'Изменить провайдера' : 'Новый провайдер'}
                        </h2>
                        
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            configMutation.mutate({
                                id: editingConfig?.id,
                                name: formData.get('name'),
                                provider_type: formData.get('provider_type'),
                                api_key: formData.get('api_key'),
                                api_base: formData.get('api_base'),
                                model_name: formData.get('model_name'),
                                is_active: editingConfig ? editingConfig.is_active : true
                            });
                        }} className="space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Название</label>
                                <input 
                                    name="name"
                                    defaultValue={editingConfig?.name}
                                    className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold placeholder:text-zinc-700" 
                                    placeholder="Напр. Основной OpenRouter" 
                                    required 
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Провайдер</label>
                                    <select 
                                        name="provider_type"
                                        defaultValue={editingConfig?.provider_type || 'openrouter'}
                                        className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold appearance-none"
                                    >
                                        <option value="openrouter">OpenRouter</option>
                                        <option value="google">Google Gemini</option>
                                        <option value="openai">OpenAI</option>
                                        <option value="cloudflare">Cloudflare</option>
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Модель</label>
                                    <input 
                                        name="model_name"
                                        defaultValue={editingConfig?.model_name}
                                        className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold" 
                                        placeholder="Напр. gpt-4o" 
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2 flex items-center gap-2">
                                    <Lock size={10} /> API Ключ
                                </label>
                                <input 
                                    name="api_key"
                                    type="password"
                                    defaultValue={editingConfig?.api_key}
                                    className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold" 
                                    placeholder="sk-..." 
                                    required={!editingConfig}
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2 flex items-center gap-2">
                                    <Globe size={10} /> Base URL / Account ID
                                </label>
                                <input 
                                    name="api_base"
                                    defaultValue={editingConfig?.api_base}
                                    className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold font-mono text-[10px]" 
                                    placeholder="Account ID для Cloudflare или Base URL" 
                                />
                            </div>

                            <div className="flex gap-4 mt-8 pt-4 border-t border-zinc-800/30">
                                <button 
                                    type="button"
                                    onClick={() => { setIsConfigModalOpen(false); setEditingConfig(null); }}
                                    className="flex-1 px-4 py-4 rounded-2xl bg-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px] hover:bg-zinc-700 transition-all"
                                >
                                    Отмена
                                </button>
                                <button 
                                    type="submit"
                                    className="flex-1 px-4 py-4 rounded-2xl bg-blue-600 text-white font-black uppercase tracking-widest text-[10px] hover:bg-blue-500 shadow-xl shadow-blue-600/20 transition-all flex items-center justify-center gap-2"
                                >
                                    <Save size={14} />
                                    Сохранить
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Prompt Modal */}
            {isPromptModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-zinc-900 border border-zinc-800 rounded-[3rem] w-full max-w-2xl shadow-2xl p-8 relative">
                        <h2 className="text-2xl font-black text-white uppercase italic italic mb-6">
                            Шаблон промпта
                        </h2>
                        
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            promptMutation.mutate({
                                slug: formData.get('slug'),
                                name: formData.get('name'),
                                template: formData.get('template'),
                                description: formData.get('description'),
                                is_system: editingPrompt ? editingPrompt.is_system : false
                            });
                        }} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Название</label>
                                    <input 
                                        name="name"
                                        defaultValue={editingPrompt?.name}
                                        className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold" 
                                        required 
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Slug (UID)</label>
                                    <input 
                                        name="slug"
                                        defaultValue={editingPrompt?.slug}
                                        readOnly={!!editingPrompt}
                                        className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold font-mono ${editingPrompt ? 'opacity-50' : ''}" 
                                        required 
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Описание</label>
                                <input 
                                    name="description"
                                    defaultValue={editingPrompt?.description}
                                    className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold" 
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Шаблон (используйте {'{key}'} для переменных)</label>
                                <textarea 
                                    name="template"
                                    defaultValue={editingPrompt?.template}
                                    rows={8}
                                    className="w-full bg-zinc-950 border border-zinc-800 p-4 rounded-2xl text-white outline-none focus:border-blue-500 transition-all font-bold custom-scrollbar" 
                                    required 
                                />
                            </div>

                            <div className="flex gap-4 mt-8 pt-4 border-t border-zinc-800/30">
                                <button 
                                    type="button"
                                    onClick={() => { setIsPromptModalOpen(false); setEditingPrompt(null); }}
                                    className="flex-1 px-4 py-4 rounded-2xl bg-zinc-800 text-zinc-400 font-black uppercase tracking-widest text-[10px]"
                                >
                                    Отмена
                                </button>
                                <button 
                                    type="submit"
                                    className="flex-1 px-4 py-4 rounded-2xl bg-blue-600 text-white font-black uppercase tracking-widest text-[10px]"
                                >
                                    Сохранить
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AISettings;
